
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import {
    Fragment,
    MutableRefObject,
    createContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { GetStaticProps, GetStaticPropsContext } from "next";
import {
    MysolutionVacancyRecruiter,
    StoryblokRecruiter,
    VacancyDescription,
    VacancyDetailProps,
    VacancyProps
} from "storyblok/components";
import {
    StoryblokBlokData,
    StoryblokComponent,
    getStoryblokApi,
    useStoryblokState
} from "@storyblok/react";
import { useRouter } from "next/router";
import { useScroll } from "framer-motion";
import parse from "html-react-parser";

import {
    ARROW_RIGHT,
    CLOCK,
    HEART,
    HEART_FILLED,
    MAP_PIN
} from "components/ui/icon";
import { Button, Icon, Modal, Page, USER } from "components";
import {
    ButtonAndHeart,
    ButtonWrapper,
    Department,
    Description,
    IconContainer,
    IconWrapper,
    IconsWrapper,
    ShareThisPopup,
    StyledContainer,
    StyledVacatureDetailPage,
    Title,
    TopLeftSection,
    TopRightSection,
    TopSection
} from "components/vacancies/VacancyDetail/VacancyDetail.styled";
import {
    EURO,
    FACEBOOK,
    LINKEDIN,
    MAIL,
    RECEIVE_MONEY,
    SHARE,
    TWITTER,
    WHATSAPP
} from "components/ui/icon/IconSet";
import { RecruiterCardContactFormWrapper } from "components/vacancies/VacancyRecruiterCard/VacancyRecruiterCard.styled";
import { VacancyRecruiterCard } from "components/vacancies/VacancyRecruiterCard/VacancyRecruiterCard";
import { VacancyStickyHeader } from "components/vacancies/VacancyStickyHeader/VacancyStickyHeader";
import { VacancyStructuredData } from "components/vacancies/VacancyStructuredData/VacancyStructuredData";
import { VacancyTopPicture } from "components/vacancies/VacancyTopPicture/VacancyTopPicture";
import VacancyForm from "components/shared/vacancy/vacancyForm/VacancyForm";

import { filterVacanciesByDepartment } from "helpers/util/filterVacanciesByDepartment";
import { getEducationCode } from "helpers/hooks/getEducationCode";
import { getStoryblokPageData } from "helpers/storyblok";
import { retrieveMysolutionVacancies } from "lib/api/vacancies";
import { useMediaQueryContext } from "helpers/hooks";
import { useWishlistCounter } from "helpers/hooks/useWishlistCounterContext";
import addToStorage from "helpers/util/localStorage/storageAdder";
import savedVacancyChecker from "helpers/util/localStorage/savedVacancyChecker";
import storageRemover from "helpers/util/localStorage/storageRemover";

export const DepartmentContext = createContext("");

const localStorageKey = "savedVacancies";

export function transformToMySolutionVacancyRecruiter(recruiterStory: {
    content: StoryblokRecruiter;
}): MysolutionVacancyRecruiter | StoryblokRecruiter {
    return {
        email: recruiterStory?.content?.email,
        name: recruiterStory?.content?.name,
        phone: recruiterStory?.content?.phone,
        showWhatsapp: !!recruiterStory?.content?.showWhatsapp
    };
}

export function getInternationalPhoneNumber(phone?: string) {
    const internationalPhone = phone?.replace("06", "316");

    internationalPhone?.replace("+", "");

    return internationalPhone ?? "";
}

function VacatureDetailPage({
    allRecruiters,
    story: initialStory,
    vacancy,
    vacancies
}: VacancyDetailProps): JSX.Element {
    const router = useRouter();
    const currentUrl = `https://www.leadhealthcare.nl${router.asPath}`;

    const stickyHeaderElementRef =
        useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
    const [showStickyHeader, setShowStickyHeader] = useState(false);
    const { scrollY } = useScroll();

    const { isDesktopView } = useMediaQueryContext();
    const { addCount, subtractCount } = useWishlistCounter();

    const [showModal, setShowModal] = useState(false);
    const [showShareThisModal, setShowShareThisModal] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [isVacancySaved, setIsVacancySaved] = useState(false);
    const story = useStoryblokState(initialStory);
    const { blocks, seo } = getStoryblokPageData(story);
    const {
        city,
        department,
        description,
        education_code,
        title,
        min_hours,
        max_hours,
        recruiter: vacancyRecruiter,
        salary,
        id,
        isZZP,
        metaDescription,
        metaTitle
    } = vacancy;

    const salaryPeriod: string = useMemo(() => {
        if (!salary?.period) return "";

        return `per ${salary.period === "Month" ? "maand" : "uur"}`;
    }, [salary?.period]);

    const storyblokRecruiter = transformToMySolutionVacancyRecruiter(
        story?.content?.recruiter as { content: StoryblokRecruiter }
    );

    const recruiter = vacancyRecruiter?.email
        ? vacancyRecruiter
        : storyblokRecruiter;

    // We need to find the recruiter's storyblok image
    const recruiterImage = allRecruiters.find(
        sbRecruiter => recruiter.email === sbRecruiter.email
    )?.image;

    const filteredVacancies = filterVacanciesByDepartment(vacancy, vacancies);

    useEffect(() => {
        scrollY.onChange(() => {
            const rect =
                stickyHeaderElementRef.current?.getBoundingClientRect?.();

            if (rect?.top <= 0) {
                setShowStickyHeader(true);
            } else {
                setShowStickyHeader(false);
            }
        });
    }, [scrollY]);

    useEffect(() => {
        setIsVacancySaved(savedVacancyChecker(vacancy, localStorageKey));
    }, [vacancy]);

    const parsedDescription: VacancyDescription =
        description && JSON.parse(description);

    return (
        <DepartmentContext.Provider value={department ?? ""}>
            <Page
                title={metaTitle || seo?.title}
                description={metaDescription || seo?.description}
                hideStickyHeader
            >
                <VacancyStructuredData vacancy={vacancy} />

                <StyledVacatureDetailPage>
                    <StyledContainer>
                        {isDesktopView && story?.content?.recruiter_picture && (
                            <VacancyRecruiterCard
                                picture={recruiterImage}
                                showContactForm={setShowContactModal}
                            />
                        )}

                        <TopSection>
                            <TopLeftSection>
                                <Department department={!!department}>
                                    {department}
                                </Department>
                                <Title>{title}</Title>
                                <IconContainer>
                                    {!!min_hours && !!max_hours && (
                                        <IconWrapper>
                                            <Icon
                                                icon={CLOCK}
                                                size={24}
                                                color="var(--color-orange-50)"
                                            />
                                            {min_hours && max_hours
                                                ? `${min_hours}-${max_hours} uur`
                                                : null}
                                        </IconWrapper>
                                    )}
                                    {getEducationCode(education_code) && (
                                        <IconWrapper>
                                            <Icon
                                                icon={USER}
                                                size={24}
                                                color="var(--color-orange-50)"
                                            />
                                            {getEducationCode(education_code)}
                                        </IconWrapper>
                                    )}
                                    <IconWrapper>
                                        <Icon
                                            icon={MAP_PIN}
                                            size={24}
                                            color="var(--color-orange-50)"
                                        />
                                        {`Regio ${city as string}`}
                                    </IconWrapper>

                                    {!!salary?.min && !!salary?.max && (
                                        <IconWrapper>
                                            <Icon
                                                icon={RECEIVE_MONEY}
                                                size={24}
                                                color="var(--color-orange-50)"
                                            />
                                            &euro;{" "}
                                            {`${salary.min}-${salary.max} ${salaryPeriod}`}
                                        </IconWrapper>
                                    )}

                                    {story?.content?.salary_indication && (
                                        <IconWrapper>
                                            <Icon
                                                icon={EURO}
                                                size={20}
                                                color="var(--color-orange-50)"
                                            />

                                            {story.content.salary_indication}
                                        </IconWrapper>
                                    )}
                                </IconContainer>

                                <ButtonAndHeart ref={stickyHeaderElementRef}>
                                    <ButtonWrapper>
                                        <Button
                                            className="gtm-apply-form"
                                            buttonType="primary"
                                            onClick={() => setShowModal(true)}
                                            icon={ARROW_RIGHT}
                                            reversedOrder
                                        >
                                            SOLLICITEER NU
                                        </Button>
                                    </ButtonWrapper>

                                    <IconsWrapper>
                                        <IconWrapper
                                            onClick={() => {
                                                if (isVacancySaved) {
                                                    storageRemover(
                                                        vacancy,
                                                        localStorageKey
                                                    );
                                                    subtractCount?.();
                                                } else {
                                                    addToStorage(
                                                        vacancy,
                                                        localStorageKey
                                                    );
                                                    addCount?.();
                                                }

                                                setIsVacancySaved(
                                                    !isVacancySaved
                                                );
                                            }}
                                            saveVacancy
                                        >
                                            <Icon
                                                icon={
                                                    isVacancySaved
                                                        ? HEART_FILLED
                                                        : HEART
                                                }
                                                size={28}
                                                color="var(--color-black)"
                                            />
                                        </IconWrapper>
                                        <IconWrapper
                                            onClick={() =>
                                                setShowShareThisModal(
                                                    !showShareThisModal
                                                )
                                            }
                                            saveVacancy
                                        >
                                            <ShareThisPopup
                                                className={`${
                                                    showShareThisModal
                                                        ? "active"
                                                        : ""
                                                }`}
                                            >
                                                <a
                                                    href={`https://www.linkedin.com/shareArticle?url=${currentUrl}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <Icon
                                                        icon={LINKEDIN}
                                                        size={28}
                                                        color="var(--color-black)"
                                                    />
                                                </a>
                                                <a
                                                    data-action="share/whatsapp/share"
                                                    href={`whatsapp://send?text=${currentUrl}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <Icon
                                                        icon={WHATSAPP}
                                                        size={28}
                                                        color="var(--color-black)"
                                                    />
                                                </a>
                                                <a
                                                    data-action="share/whatsapp/share"
                                                    href={`mailto:?subject=Vacature Lead Healthcare&body=${currentUrl}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <Icon
                                                        icon={MAIL}
                                                        size={28}
                                                        color="var(--color-black)"
                                                    />
                                                </a>
                                                <a
                                                    data-action="share/whatsapp/share"
                                                    href={`https://twitter.com/intent/tweet?text=Vacature Lead Healthcare ${currentUrl}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <Icon
                                                        icon={TWITTER}
                                                        size={28}
                                                        color="var(--color-black)"
                                                    />
                                                </a>
                                                <a
                                                    data-action="share/whatsapp/share"
                                                    href={`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <Icon
                                                        icon={FACEBOOK}
                                                        size={28}
                                                        color="var(--color-black)"
                                                    />
                                                </a>
                                            </ShareThisPopup>
                                            <Icon
                                                icon={SHARE}
                                                size={28}
                                                color="var(--color-black)"
                                            />
                                        </IconWrapper>
                                    </IconsWrapper>
                                </ButtonAndHeart>

                                {recruiter &&
                                    (storyblokRecruiter as StoryblokRecruiter)
                                        .showWhatsapp &&
                                    recruiter.phone && (
                                        <Button
                                            buttonType="secondary"
                                            icon={WHATSAPP}
                                            href={`https://wa.me/${getInternationalPhoneNumber(
                                                recruiter.phone
                                            )}`}
                                            target="_blank"
                                        >
                                            WHATSAPP DE RECRUITER
                                        </Button>
                                    )}
                            </TopLeftSection>

                            <TopRightSection>
                                <VacancyTopPicture
                                    picture={story.content.top_picture}
                                    reverse={
                                        story?.content?.reverse_top_picture
                                    }
                                />
                            </TopRightSection>
                        </TopSection>

                        <VacancyStickyHeader
                            visible={showStickyHeader}
                            vacancy={vacancy}
                            showForm={setShowModal}
                            showContactForm={setShowContactModal}
                            isZZP={false}
                            salary={story?.content?.salary_indication}
                        />

                        <Description ulNoMargin>
                            {Object.keys(parsedDescription).map(key => {
                                const heading =
                                    vacancy.descriptionHeadings?.[
                                        key as keyof VacancyDescription
                                    ];

                                const content =
                                    parsedDescription[
                                        key as keyof VacancyDescription
                                    ];

                                return (
                                    <Fragment key={key}>
                                        {heading && <h2>{heading}</h2>}
                                        {content && parse(content)}
                                    </Fragment>
                                );
                            })}
                        </Description>

                        <Modal
                            onClose={() => setShowModal(!showModal)}
                            isOpen={showModal}
                            showCloseButton={false}
                        >
                            <VacancyForm
                                isZZP={isZZP}
                                jobId={id}
                                jobTitle={title}
                                label={department}
                                jobEducationLevel={getEducationCode(
                                    education_code
                                )}
                                hours={
                                    min_hours && max_hours
                                        ? `${min_hours}-${max_hours} uur`
                                        : ""
                                }
                                jobLocation={`Regio ${city as string}`}
                                closeModal={() => setShowModal(false)}
                            />
                        </Modal>

                        <Modal
                            onClose={() =>
                                setShowContactModal(!showContactModal)
                            }
                            isOpen={showContactModal}
                        >
                            <RecruiterCardContactFormWrapper>
                                <StoryblokComponent
                                    blok={{
                                        ...story?.content?.contact_form[0],
                                        inVacancyRecruiterCard: true
                                    }}
                                />
                            </RecruiterCardContactFormWrapper>
                        </Modal>
                    </StyledContainer>
                </StyledVacatureDetailPage>
                {blocks?.map((block: StoryblokBlokData) => {
                    const additionalProps = block.component ===
                        "VacancySlider" && {
                        vacancies: filteredVacancies
                    };
                    return (
                        <StoryblokComponent
                            key={block._uid}
                            blok={block}
                            {...additionalProps}
                        />
                    );
                })}
            </Page>
        </DepartmentContext.Provider>
    );
}

 const _getStaticProps: GetStaticProps = async ({
    params,
    preview
}: GetStaticPropsContext) => {
    const initialVacancies = await retrieveMysolutionVacancies();

    const vacancy =
        initialVacancies &&
        initialVacancies.find((vacancy: VacancyProps) => {
            return vacancy.slug === params?.slug;
        });

    const storyblokApi = getStoryblokApi();

    if (!vacancy || !storyblokApi) {
        return { notFound: true };
    }

    const draftMode = preview
        ? "draft"
        : (process.env.NEXT_PUBLIC_STORYBLOK_VERSION as "draft" | "published");
    const isPreview = draftMode === "draft";

    const { data: allStories } = await storyblokApi.getStories({
        starts_with: "zorgprofessionals/vacature-detail-labels/"
    });
    const possibleLabels = allStories.stories.map(s => s.slug);
    const department = vacancy?.department?.toLowerCase();

    let storyData;
    if (department && possibleLabels.includes(department)) {
        const { data } = await storyblokApi.getStory(
            `/zorgprofessionals/vacature-detail-labels/${department}`,
            {
                resolve_relations: "VacatureDetailLabel.recruiter",
                version: draftMode
            }
        );
        storyData = data;
    } else {
        const { data } = await storyblokApi.getStory(
            `/zorgprofessionals/vacature-detail-labels/default`,
            {
                resolve_relations: "VacatureDetailLabel.recruiter",
                version: draftMode
            }
        );
        storyData = data;
    }

    // Get all recruiters from the recruiter folder in storyblok
    const {
        data: { stories: recruitersFolder }
    } = await storyblokApi.getStories({
        starts_with: "recruiters/"
    });
    const allRecruiters = recruitersFolder.map(recruiter => recruiter.content);

    return {
        props: {
            allRecruiters: allRecruiters ?? [],
            story: storyData?.story ?? null,
            vacancies: initialVacancies,
            vacancy
        },
        revalidate: isPreview ? 1 : 3600
    };
};

export async function getStaticPaths() {
    const initialVacancies = await retrieveMysolutionVacancies();

    const paths =
        initialVacancies &&
        initialVacancies.map(vacancy => {
            const slug = vacancy?.slug;

            if (slug) {
                return { params: { slug } };
            }
        });

    return {
        fallback: "blocking",
        paths: paths || []
    };
}

export default VacatureDetailPage;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/zorgprofessionals/vacatures/[slug]',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  